import { crewClaimResolutionMethodEnumName, crewClaimRollupStatusCodeName, crewClaimStatuses, rmaProviderName, shipmentMethodName, } from '../../enums/crew.js';
import { shippingClaimReasonName, shippingClaimResolutionMethodName, shippingClaimStatusName, } from '../../enums/gsp.js';
import { trackingStatusCodeName } from '../../enums/tracking.js';
import { claimSourceName } from './claimSource.js';
import { Mcp } from './mcp.js';
/** How each MCP tool reads to people: its title for MCP clients, and what the in-app chat shows while it runs. */
export const mcpToolDisplay = {
    [Mcp.Tool.listStores]: {
        title: 'List stores',
        activity: 'Checking your store',
    },
    [Mcp.Tool.listOrders]: {
        title: 'List orders',
        activity: 'Looking up orders',
    },
    [Mcp.Tool.getOrder]: {
        title: 'Get an order',
        activity: 'Looking up the order',
    },
    [Mcp.Tool.getOrderFulfillments]: {
        title: 'Get order tracking',
        activity: 'Looking up fulfillments',
    },
    [Mcp.Tool.getOrderEligibility]: {
        title: 'Get an order’s return and warranty eligibility',
        activity: 'Checking what the order is eligible for',
    },
    [Mcp.Tool.listClaims]: {
        title: 'List returns and warranties',
        activity: 'Looking up claims',
    },
    [Mcp.Tool.getClaim]: {
        title: 'Get a return or warranty',
        activity: 'Looking up the claim',
    },
    [Mcp.Tool.getClaimMoney]: {
        title: 'Get the money on a return or warranty',
        activity: 'Working through the claim’s money',
    },
    [Mcp.Tool.getClaimRisk]: {
        title: 'Get a return or warranty’s risk',
        activity: 'Checking the claim’s risk',
    },
    [Mcp.Tool.getClaimLineage]: {
        title: 'Get a return or warranty’s order lineage',
        activity: 'Tracing the claim back to the original order',
    },
    [Mcp.Tool.listShippingClaims]: {
        title: 'List shipping protection claims',
        activity: 'Looking up shipping claims',
    },
    [Mcp.Tool.getShippingClaim]: {
        title: 'Get a shipping protection claim',
        activity: 'Looking up the shipping claim',
    },
    [Mcp.Tool.listRegistrations]: {
        title: 'List product registrations',
        activity: 'Looking up registrations',
    },
    [Mcp.Tool.getRegistration]: {
        title: 'Get a product registration',
        activity: 'Looking up the registration',
    },
    [Mcp.Tool.search]: {
        title: 'Search Corso Help',
        activity: 'Searching the help center',
    },
    [Mcp.Tool.fetch]: {
        title: 'Read a Corso Help article',
        activity: 'Reading a help article',
    },
    [Mcp.Tool.listHelpArticles]: {
        title: 'List Corso Help articles',
        activity: 'Browsing the help center',
    },
    [Mcp.Tool.describeData]: {
        title: 'Describe Corso data',
        activity: 'Reading the data guide',
    },
    [Mcp.Tool.query]: {
        title: 'Query Corso data',
        activity: 'Querying your data',
    },
    [Mcp.Tool.getStoreSettings]: {
        title: 'Get store settings',
        activity: 'Reading your settings',
    },
    [Mcp.Tool.listAutomations]: {
        title: 'List automations',
        activity: 'Looking up automations',
    },
    [Mcp.Tool.getAutomation]: {
        title: 'Get an automation',
        activity: 'Reading the automation',
    },
    [Mcp.Tool.getOrderFacts]: {
        title: 'Get an order’s automation facts',
        activity: 'Reading the order’s automation facts',
    },
    [Mcp.Tool.evaluateAutomations]: {
        title: 'Check which automations apply to an order',
        activity: 'Checking which automations apply',
    },
    [Mcp.Tool.listReturnLocations]: {
        title: 'List return locations',
        activity: 'Looking up return locations',
    },
    [Mcp.Tool.listClaimReasons]: {
        title: 'List return and warranty reasons',
        activity: 'Looking up reasons',
    },
    [Mcp.Tool.listProductGroups]: {
        title: 'List product groups',
        activity: 'Looking up product groups',
    },
    [Mcp.Tool.listCustomFields]: {
        title: 'List custom fields',
        activity: 'Looking up custom fields',
    },
    [Mcp.Tool.listShippingPlusRates]: {
        title: 'List Shipping Plus rates',
        activity: 'Looking up Shipping Plus rates',
    },
    [Mcp.Tool.listIntegrations]: {
        title: 'List integrations',
        activity: 'Checking your integrations',
    },
    [Mcp.Tool.listStoreUsers]: {
        title: 'List store users',
        activity: 'Looking up store users',
    },
    [Mcp.Tool.describePortalStyle]: {
        title: 'Describe portal style',
        activity: 'Looking at your portal style',
    },
    [Mcp.Tool.importBrandFromSite]: {
        title: 'Import brand from site',
        activity: 'Reading your website’s brand',
    },
    [Mcp.Tool.savePortalCss]: {
        title: 'Save portal CSS',
        activity: 'Saving your portal style',
    },
    [Mcp.Tool.revertPortalCss]: {
        title: 'Revert portal CSS',
        activity: 'Putting your previous portal style back',
    },
    [Mcp.Tool.generatePortalCss]: {
        title: 'Generate portal CSS',
        activity: 'Styling your portal',
    },
    [Mcp.Tool.auditPortalCss]: {
        title: 'Check portal',
        activity: 'Checking your portal',
    },
};
const claimStatuses = Object.values(crewClaimStatuses);
/** The Corso app's labels for the codes MCP records carry, grouped the way the server's instructions name them to the model. */
export const mcpCodeLabelGroups = [
    ['Return or warranty status', crewClaimRollupStatusCodeName],
    [
        'Line item status',
        Object.fromEntries(claimStatuses.map(({ code, name }) => [code, name])),
    ],
    [
        'Line item status detail',
        Object.fromEntries(claimStatuses
            .flatMap(({ claimStatusDetail }) => claimStatusDetail)
            .map(({ code, name }) => [code, name])),
    ],
    ['Resolution', crewClaimResolutionMethodEnumName],
    ['Return shipment method', shipmentMethodName],
    ['RMA provider', rmaProviderName],
    ['Tracking status', trackingStatusCodeName],
    ['Created from', claimSourceName],
    ['Shipping claim status', shippingClaimStatusName],
    ['Shipping claim reason', shippingClaimReasonName],
    ['Shipping claim resolution', shippingClaimResolutionMethodName],
];
// every code to its label, for showing a record's values to people; Variant_Exchange is the one code two groups label differently, and the resolution's "Exchange" wins
export const mcpCodeLabel = Object.fromEntries(mcpCodeLabelGroups.flatMap(([, labels]) => Object.entries(labels)));
//# sourceMappingURL=mcpDisplay.js.map